/**
 * Auto-advancing image background for the hero. Slides a horizontal filmstrip of images
 * on a fixed timer (never drag/swipe-driven) and loops seamlessly: the DOM includes a
 * clone of the first slide appended after the last, so the track can keep sliding forward
 * forever — once it reaches that clone, it snaps back to the real first slide with the
 * transition disabled for one frame, invisible to the viewer since the clone and the
 * original look identical.
 *
 * Does not touch anything outside itself — the hero's text/button overlay lives in a
 * separate, unrelated part of the DOM and is unaffected by this element's animation.
 */
class HeroRotator extends HTMLElement {
  #track;
  #index = 0;
  #realCount = 0;
  #timer = null;

  connectedCallback() {
    this.#track = this.querySelector('.hero__rotator-track');
    if (!this.#track) return;

    // Last child is the appended clone of the first slide, used only for the loop-around.
    this.#realCount = this.#track.children.length - 1;
    if (this.#realCount < 2) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const seconds = Number(this.getAttribute('interval')) || 8;
    this.#timer = setInterval(() => this.#advance(), seconds * 1000);

    // bfcache restores (e.g. clicking back to the homepage) resume this element exactly as
    // it was left — including mid-rotation — without re-running connectedCallback. Force the
    // first slide back on screen whenever that happens, so "back" always shows slide 1 again.
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    if (this.#timer) clearInterval(this.#timer);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  #onPageShow = (event) => {
    if (!event.persisted) return;
    this.#index = 0;
    this.#track.classList.add('hero__rotator-track--no-transition');
    this.#track.style.transform = 'translateX(0)';
    requestAnimationFrame(() => {
      this.#track.classList.remove('hero__rotator-track--no-transition');
    });
  };

  #advance() {
    this.#index += 1;
    this.#track.style.transform = `translateX(-${this.#index * 100}%)`;

    if (this.#index === this.#realCount) {
      this.#track.addEventListener('transitionend', () => this.#loopToStart(), { once: true });
    }
  }

  #loopToStart() {
    this.#track.classList.add('hero__rotator-track--no-transition');
    this.#index = 0;
    this.#track.style.transform = 'translateX(0)';

    requestAnimationFrame(() => {
      this.#track.classList.remove('hero__rotator-track--no-transition');
    });
  }
}

if (!customElements.get('hero-rotator')) customElements.define('hero-rotator', HeroRotator);
